import { axiosInstance } from "libs/axiosInstance";

import { formatAxiosConfig } from "@becon-team/becon-fe-lib/my-axios";
import { GetGlobalPolicy, GetGlobalPolicyResponseSchema } from "@becon-team/becon-fe-lib/services";
import { BeconAccountTypes } from "@becon-team/becon-fe-lib/my-types";
import { convertObjToQueryStr } from "@becon-team/becon-fe-lib/utils";
import { validateZodSchema } from "@becon-team/becon-fe-lib/my-zod";
import type { TAgeGroup, TGender } from "api/@simpleQuiz/simple_quiz_types";
import { objectToQueryString } from "utils/@common/utils_common";
import {
  DeleteScalpsetCandidate,
  GetCrownAnalysisHistory,
  GetCrownAnalysisHistoryResponseSchema,
  GetCrownAnalysisResult,
  GetCrownAnalysisResultResponseSchema,
  GetCrownAnalysisStatus,
  GetCrownAnalysisStatusResponseSchema,
  GetMicroScalpAnalysisTrend,
  GetMicroScalpAnalysisTrendResponseSchema,
  GetScalpAnalysisStatus,
  GetScalpAnalysisStatusResponseSchema,
  GetScalpAverageScore,
  GetScalpAverageScoreResponseSchema,
  GetScalpHistory,
  GetScalpHistoryResponseSchema,
  GetScalpHistoryStatistics,
  GetScalpHistoryStatisticsResponseSchema,
  GetScalpsetPolling,
  GetScalpsetPollingResponseSchema,
  GetScalpsetResult,
  GetScalpsetResultResponseSchema,
  GetScalpSetStatistics,
  GetScalpSetStatisticsResponseDtoSchema,
  PostScalpset,
  PostScalpsetDuplicate,
} from "./scalp.schema";
import type { IScalpResultInfo, TAnalysisItemInfo, TScalpAnalysisAverageInfos, TScalpRankInfos } from "./scalp_types";
import { GetScalpset, GetScalpsetResponseSchema } from "modules/scalp/services/scalp.dto";

/** GET: 두피 분석 히스토리 조회 */
export const getScalpHistory: GetScalpHistory = async ({
  userId,
  analysisItem,
  scalpPoint,
  skip,
  take,
  timeOffset,
  analysisDate,
  accountType = BeconAccountTypes.AccountTypeEnum.B2C,
  fromDate,
  toDate,
}) => {
  const queryString = objectToQueryString({
    analysisItem,
    scalpPoint,
    skip,
    take,
    timeOffset,
    analysisDate,
    accountType,
    fromDate,
    toDate,
  });
  const serverRes = await axiosInstance.get(`/my/user/${userId}/scalp/histories?${queryString}`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetScalpHistoryResponseSchema,
    formatAxiosConfig(serverRes.config),
  );
  return validatedData;
};

/** GET: 두피 마이크로 분석 트렌드 조회 */
export const getMicroScalpAnalysisTrend: GetMicroScalpAnalysisTrend = async ({ userId, timeOffset }) => {
  const queryStr = convertObjToQueryStr({
    timeOffset,
  });
  const serverRes = await axiosInstance.get(`/my/user/${userId}/scalp/trend?${queryStr}`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetMicroScalpAnalysisTrendResponseSchema,
    formatAxiosConfig(serverRes.config),
  );

  return validatedData;
};

/** GET: 두피 분석 날짜별 통계 히스토리 조회 */
export const getScalpHistoryStatistics: GetScalpHistoryStatistics = async ({
  accountType,
  orderBy,
  userId,
  unit,
  timeOffset,
  skip,
  take,
}) => {
  const queryStr = convertObjToQueryStr({ accountType, orderBy, unit, timeOffset, skip, take });

  const serverRes = await axiosInstance.get(`/my/user/${userId}/scalp/stat?${queryStr}`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetScalpHistoryStatisticsResponseSchema,
    formatAxiosConfig(serverRes.config),
  );
  return validatedData;
};

/** GET: scalp set 분석 날짜별 통계 히스토리 조회 */
export const getScalpSetStatistics: GetScalpSetStatistics = async ({ orderBy, userId, timeOffset, skip, take }) => {
  const queryStr = convertObjToQueryStr({ orderBy, timeOffset, skip, take });

  const serverRes = await axiosInstance.get(`/my/user/${userId}/scalp-set/stat?${queryStr}`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetScalpSetStatisticsResponseDtoSchema,
    formatAxiosConfig(serverRes.config),
  );
  return validatedData;
};

export const getScalpResult = async ({ userId, analysisId }: { userId: string; analysisId: string | undefined }) => {
  const res = await axiosInstance.get<IScalpResultInfo>("/my/user/" + userId + "/scalp/" + analysisId);
  return res.data;
};

/** 공용 정책 조회  API */
export const getGlobalPolicy = async () => {
  const serverRes = await axiosInstance.get<GetGlobalPolicy>(`/policy/global`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetGlobalPolicyResponseSchema,
    formatAxiosConfig(serverRes.config),
  );
  return validatedData;
};

/** 두피 분석 삭제 API */
export const deleteScalpResult = async (userId: string, analysisId: string): Promise<void> => {
  const res = await axiosInstance.delete(`/my/user/${userId}/scalp/${analysisId}`);
  return res.data;
};

/** 두피 분석 랭킹  API */
export const getScalpRank = async (
  userId: string,
  analysisId: string | undefined,
  gender: TGender,
  ageGroup: TAgeGroup,
) => {
  const res = await axiosInstance.get<TScalpRankInfos>(
    `/my/user/${userId}/scalp/analysis/${analysisId}/ranks?gender=${gender}&ageGroup=${ageGroup}&item=ALL`,
  );
  return res.data;
};

/** 두피 분석 평균 API */
export const getScalpAnalysisAverage = async (userId: string, gender: TGender, ageGroup: TAgeGroup) => {
  const res = await axiosInstance.get<TScalpAnalysisAverageInfos>(
    `/my/user/${userId}/scalp/averages?gender=${gender}&ageGroup=${ageGroup}&item=ALL`,
  );
  return res.data;
};
// 두피진단 + 문진 보정 두피타입 및 두피이슈 조회
export const getScalpType = ({ user_id, diagnosis_items }: { user_id: string; diagnosis_items?: TAnalysisItemInfo }) =>
  axiosInstance.post("/my/user/" + user_id + "/scalp/diagnosis/scalp-types", diagnosis_items);

/** GET: 정수리 분석 결과 조회 */
export const getCrownAnalysisResult: GetCrownAnalysisResult = async ({ userId, analysisId }) => {
  const serverRes = await axiosInstance.get(`/my/user/${userId}/hair-macro/${analysisId}`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetCrownAnalysisResultResponseSchema,
    formatAxiosConfig(serverRes.config),
  );

  return validatedData;
};

/** GET: 정수리 분석 상태 조회 (query x) */
export const getCrownAnalysisStatus: GetCrownAnalysisStatus = async ({ analysisId, userId }) => {
  const serverRes = await axiosInstance.get(`/my/user/${userId}/hair-macro/status/${analysisId}`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetCrownAnalysisStatusResponseSchema,
    formatAxiosConfig(serverRes.config),
  );

  return validatedData;
};

/** GET: 정수리 분석 히스토리 조회 */
export const getCrownAnalysisHistory: GetCrownAnalysisHistory = async ({ status, skip, take, userId }) => {
  const queryStr = convertObjToQueryStr({
    skip,
    take,
  });

  const serverRes = await axiosInstance.get(`/my/user/${userId}/hair-macro/history/${status}?${queryStr}`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetCrownAnalysisHistoryResponseSchema,
    formatAxiosConfig(serverRes.config),
  );
  return validatedData;
};

/** GET: 두피 분석 평균 조회 */
export const getScalpAverageScore: GetScalpAverageScore = async ({ userId }) => {
  const serverRes = await axiosInstance.get(`/my/user/${userId}/scalp/averages`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetScalpAverageScoreResponseSchema,
    formatAxiosConfig(serverRes.config),
  );

  return validatedData;
};

/** GET: 두피 분석 상태 조회 (query x) */
export const getScalpAnalysisStatus: GetScalpAnalysisStatus = async ({ userId, jobId }) => {
  const serverRes = await axiosInstance.get(`/my/user/${userId}/scalp/v3/analysis?jobId=${jobId}`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetScalpAnalysisStatusResponseSchema,
    formatAxiosConfig(serverRes.config),
  );

  return validatedData;
};

/** GET: ScalpSet 조회 */
export const getScalpSet: GetScalpset = async ({ userId, skip, take, status }) => {
  const queryStr = convertObjToQueryStr({
    skip,
    take,
    status,
  });
  const serverRes = await axiosInstance.get(`/my/user/${userId}/scalp-set?${queryStr}`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetScalpsetResponseSchema,
    formatAxiosConfig(serverRes.config),
  );

  return validatedData;
};

/** GET: ScalpSet Polling  query(x) */
export const getScalpSetPolling: GetScalpsetPolling = async ({ userId, id }) => {
  const serverRes = await axiosInstance.get(`/my/user/${userId}/scalp-set/polling/${id}`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetScalpsetPollingResponseSchema,
    formatAxiosConfig(serverRes.config),
  );

  return validatedData;
};

/** GET: ScalpSet 결과 조회 */
export const getScalpSetResult: GetScalpsetResult = async ({ userId, id }) => {
  const serverRes = await axiosInstance.get(`/my/user/${userId}/scalp-set/${id}/result`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetScalpsetResultResponseSchema,
    formatAxiosConfig(serverRes.config),
  );

  return validatedData;
};

/** DELETE: ScalpSetCandidate 삭제 */
export const deleteScalpSetCandidate: DeleteScalpsetCandidate = async ({ userId, id }) => {
  const serverRes = await axiosInstance.delete(`/my/user/${userId}/scalp-set/candidate/${id}`);

  return serverRes.data;
};

/** POST: ScalpSet 생성 */
export const postScalpSet: PostScalpset = async ({ userId }) => {
  const serverRes = await axiosInstance.post(`/my/user/${userId}/scalp-set`);

  return serverRes.data;
};

/** Post ScalpSet 복제 */
export const postScalpSetDuplicate: PostScalpsetDuplicate = async ({ userId, id }) => {
  const serverRes = await axiosInstance.post(`/my/user/${userId}/scalp-set/${id}/duplicate`);

  return serverRes.data;
};
